import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import copyToClipboard from 'copy-to-clipboard';

import { SR_API_ACTION_ADMIN_VALIDATE_PARTNER_SIGNUP } from 'api/actionTypes';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { formatPrice, formatPercentage, formatPhoneNumber } from 'common/formatters';
import {
  DATA_TABLE_SOURCE_ADMIN_PARTNERS,
  DATA_TABLE_VIEW_ID_ADMIN_PARTNERS_ALL,
  DATA_TABLE_VIEW_ID_ADMIN_PARTNERS_REVIEW,
} from 'common/dataTableConstants';

import postWithState from 'client/actions/postWithState';
import { actionDataTableLoadItems, actionDataTableRemoveItem } from 'client/helpers/dataTables';

import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrPartnerEditModal from 'client/components/SrPartnerEditModal';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrPartnerNotesModal from 'client/components/SrPartnerNotesModal';

function getDataTableSettingsFromProps(props) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_ADMIN_PARTNERS,
    rootViewId: props.adminPartnerViewId || DATA_TABLE_VIEW_ID_ADMIN_PARTNERS_ALL,
    initialViewSettings: {
      filter: {},
      sort: {
        sortOrder: 'desc',
      },
      customParams: {
        adminPartners: {},
      },
      loadAllData: true,
    },
    itemsPropName: 'partners',
    count: 25,
    sortUserSettingsFieldId: 'adminPartners.sorting',
  };

  if (props.sorting) {
    dataTableSettings.initialViewSettings.sort = props.sorting;
  }

  if (props.accountManagerUserId && props.accountManagerUserId.length > 0) {
    dataTableSettings.initialViewSettings.filter.accountManagerUserId = props.accountManagerUserId; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_accountManager:${props.accountManagerUserId}`; // eslint-disable-line max-len
  }

  if (
    Array.isArray(props.leasingOperationsManagerId) &&
    props.leasingOperationsManagerId.length > 0
  ) {
    dataTableSettings.initialViewSettings.filter.leasingOperationsManagerUserId =
      props.leasingOperationsManagerId; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_leasingManager:${props.leasingOperationsManagerId}`; // eslint-disable-line max-len
  }

  if (props.salesRepUserId && props.salesRepUserId.length > 0) {
    dataTableSettings.initialViewSettings.filter.salesRepUserId = props.salesRepUserId; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_salesRep:${props.salesRepUserId}`; // eslint-disable-line max-len
  }

  if (props.partnerType && props.partnerType.length > 0) {
    dataTableSettings.initialViewSettings.filter.agentSignupReasonType = props.partnerType; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_partnerType:${props.partnerType}`; // eslint-disable-line max-len
  }

  if (props.adminPartnerViewId === DATA_TABLE_VIEW_ID_ADMIN_PARTNERS_REVIEW) {
    dataTableSettings.initialViewSettings.customParams.adminPartners.noValidatedPartners = true; // eslint-disable-line max-len
    dataTableSettings.rootViewId = `${dataTableSettings.rootViewId}_pendingInvalidSignup`; // eslint-disable-line max-len
  }

  return dataTableSettings;
}

const SrPartnerUsers = ({ partner }) => {
  const [showAll, setShowAll] = useState(partner.partnerUsers.length <= 3);
  const partnerUsers = showAll ? partner.partnerUsers : partner.partnerUsers.slice(0, 3);

  return (
    <>
      {partnerUsers.map((partnerUser, index) => (
        <SrBox
          key={partnerUser.phone}
          marginTop={index > 0 ? '12px' : null}
          paddingTop={index > 0 ? '10px' : null}
          borderTop={index > 0 ? '1px solid gray' : null}
        >
          <SrText display="block" weight="500">
            {partnerUser.name}
          </SrText>
          {partnerUser.phone && (
            <SrBox
              cursor="pointer"
              onClick={() => copyToClipboard(formatPhoneNumber(partnerUser.phone))}
            >
              <SrText display="block">{formatPhoneNumber(partnerUser.phone)}</SrText>
            </SrBox>
          )}
          {partnerUser.email && (
            <SrBox cursor="pointer" onClick={() => copyToClipboard(partnerUser.email)}>
              <SrText display="block">{partnerUser.email}</SrText>
            </SrBox>
          )}
          {partnerUser.partnerAppInstalled && (
            <SrText display="block" weight="500" fontStyle="italic">
              Leasing App
            </SrText>
          )}
        </SrBox>
      ))}
      {!showAll && (
        <SrBox
          marginTop="12px"
          paddingTop="10px"
          borderTop="1px solid gray"
          onClick={() => setShowAll(true)}
          cursor="pointer"
          textAlign="center"
        >
          <SrText decoration="underline">Read more</SrText>
        </SrBox>
      )}
    </>
  );
};

class SrDataTableAdminPartners extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    adminPartnerViewId: PropTypes.string,
    location: PropTypes.object,
    isReview: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      editingPartner: null,
      editingNotesPartner: null,
    };
  }

  _reloadPartner = (partnerId) => {
    const { dispatch } = this.props;

    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_ADMIN_PARTNERS,
      viewId: DATA_TABLE_VIEW_ID_ADMIN_PARTNERS_ALL,
      count: 1,
      filter: {
        'partners.id': partnerId,
      },
    });
  };

  _hidePartner = (partnerId) => {
    const { dispatch } = this.props;

    dispatch(actionDataTableRemoveItem, {
      dataSource: DATA_TABLE_SOURCE_ADMIN_PARTNERS,
      entityId: partnerId,
    });
  };

  _onEditPartnerClick = (partner) => {
    this.setState({
      editingPartner: partner,
    });
  };

  _onEditPartnerNotesClick = (partner) => {
    this.setState({
      editingNotesPartner: partner,
    });
  };

  _onConfirmPartnerSignUpValidation = (validation, displayName, partnerId) => {
    const { dispatch } = this.props;
    const confirmMessage = `Are you sure you want to mark ${displayName} as ${
      validation ? 'invalid' : 'valid'
    }?`;
    if (!global.confirm(confirmMessage)) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_VALIDATE_PARTNER_SIGNUP,
      payload: {
        partnerId,
        signupInvalid: validation,
      },
      onSuccess: () => {
        this._hidePartner(partnerId);
      },
    });
  };

  _onEditPartnerModalSuccess = () => {
    const { editingPartner } = this.state;

    this._reloadPartner(editingPartner.id);
    this._onEditPartnerModalClose();
  };

  _onEditPartnerModalClose = () => {
    this.setState({
      editingPartner: null,
    });
  };

  _onEditPartnerNotesModalClose = () => {
    this.setState({
      editingNotesPartner: null,
    });
  };

  _autoScrollFunction = () => {
    if (location?.search?.includes('scrollToPartner')) {
      const element = document.getElementById('scrollHereForPartner');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  render() {
    const { dispatch, currentUser, adminPartnerViewId, location, isReview } = this.props;
    const { editingPartner, editingNotesPartner } = this.state;

    return (
      <div className="SrTableContainer">
        <SrDataTableConnected
          {...getDataTableSettingsFromProps({
            ...currentUser.settings.adminPartners,
            adminPartnerViewId,
          })}
        >
          {({ partners, viewSettings, dataTableApiState }) => (
            <div>
              {dataTableApiState.error && <pre>Error: {dataTableApiState.error}</pre>}
              <SrDataTableUI
                dataTableViewSettings={viewSettings}
                sticky
                autoScrollFunction={this._autoScrollFunction}
                headers={[
                  ...(isReview
                    ? [
                        {
                          content: 'review',
                        },
                      ]
                    : []),
                  {
                    content: 'logo',
                  },
                  {
                    content: 'name',
                    dataTableFieldId: 'displayName',
                  },
                  {
                    content: 'partner users',
                  },
                  {
                    content: 'leases past 18 months',
                    dataTableFieldId: 'leasesPastEighteenMonths',
                  },
                  {
                    content: 'sales rep',
                    dataTableFieldId: 'salesRepName',
                  },
                  {
                    content: 'account manager',
                    dataTableFieldId: 'accountManagerName',
                  },
                  {
                    content: 'leasing operations manager',
                  },
                  {
                    content: 'created date',
                    dataTableFieldId: 'createdDate',
                  },
                  {
                    content: 'date of first door active',
                    dataTableFieldId: 'firstMarketingPartnerListingCreatedDate',
                  },
                  {
                    content: 'going to market count',
                    dataTableFieldId: 'goingToMarketCount',
                  },
                  {
                    content: 'doors active',
                    dataTableFieldId: 'listingsActive',
                  },
                  {
                    content: 'total doors submitted',
                    dataTableFieldId: 'listingsSubmitted',
                  },
                  {
                    content: 'total doors leased',
                    dataTableFieldId: 'listingsLeased',
                  },
                  {
                    content: 'withdrawn after being active',
                    dataTableFieldId: 'activeWithdrawals',
                  },
                  {
                    content: 'withdrawn from going to market',
                    dataTableFieldId: 'goingToMarketWithdrawals',
                  },
                  {
                    content: 'gotcha gong - post sign up',
                    dataTableFieldId: 'gotchaGongCount',
                  },
                  {
                    content: 'gotcha gong - post lease',
                    dataTableFieldId: 'gotchaGongPostLeaseCount',
                  },
                  {
                    content: 'commission',
                    dataTableFieldId: 'commissionPercentage',
                  },
                  {
                    content: 'closing ratio %',
                  },
                  {
                    content: 'expected yearly revenue',
                  },
                  {
                    content: 'actual revenue all-time',
                  },
                  {
                    content: 'pacing %',
                  },
                ]}
                rows={partners.map((partner) => {
                  const averageRentPriceForCalculation =
                    partner.listingsLeased >= 3 ? partner.averageRentPrice : 1250;
                  const expectedYearlyRevenue =
                    (partner.leasesPastEighteenMonths &&
                      partner.leaseFeePercentage &&
                      Math.round(
                        partner.leasesPastEighteenMonths *
                          0.45 *
                          averageRentPriceForCalculation *
                          partner.leaseFeePercentage,
                      )) ||
                    null;

                  return {
                    cells: [
                      ...(isReview
                        ? [
                            {
                              content: (
                                <div
                                  id={
                                    location?.search?.includes(partner.slug)
                                      ? 'scrollHereForPartner'
                                      : null
                                  }
                                  style={
                                    location?.search?.includes(partner.slug)
                                      ? { scrollMarginTop: '90px' }
                                      : null
                                  }
                                >
                                  <SrBox
                                    marginTop="5px"
                                    cursor="pointer"
                                    onClick={() =>
                                      this._onConfirmPartnerSignUpValidation(
                                        false,
                                        partner.displayName,
                                        partner.id,
                                      )
                                    }
                                  >
                                    <SrText color="blue300">Valid</SrText>
                                  </SrBox>
                                  <SrBox
                                    marginTop="5px"
                                    cursor="pointer"
                                    onClick={() =>
                                      this._onConfirmPartnerSignUpValidation(
                                        true,
                                        partner.displayName,
                                        partner.id,
                                      )
                                    }
                                  >
                                    <SrText color="red">Invalid</SrText>
                                  </SrBox>
                                </div>
                              ),
                            },
                          ]
                        : []),
                      {
                        content: (
                          <SrBox maxWidth="200px" maxHeight="100px">
                            {partner.logoUrl ? (
                              <SrLink to={`/partner/${partner.slug}`}>
                                <img
                                  style={{ width: '100%', height: '100%' }}
                                  src={partner.logoUrl}
                                  alt={`${partner.displayName} logo`}
                                />
                              </SrLink>
                            ) : (
                              '--'
                            )}
                          </SrBox>
                        ),
                      },
                      {
                        content: (
                          <>
                            <SrLink to={`/partner/${partner.slug}`}>{partner.displayName}</SrLink>
                            <SrBox
                              marginTop="5px"
                              cursor="pointer"
                              onClick={() => this._onEditPartnerNotesClick(partner)}
                            >
                              <SrText color="blue300">Notes</SrText>
                            </SrBox>
                          </>
                        ),
                      },
                      {
                        content: <SrPartnerUsers partner={partner} />,
                      },
                      { content: partner.leasesPastEighteenMonths || '--' },
                      { content: partner.salesRepName || '--' },
                      { content: partner.accountManagerName || '--' },
                      {
                        content: partner.leasingOperationsManagerName || '--',
                      },
                      {
                        content: momentWithAustinTimezone(partner.createdDate).format('MM/DD/YYYY'),
                      },
                      {
                        content: partner.firstMarketingPartnerListingCreatedDate
                          ? momentWithAustinTimezone(
                              partner.firstMarketingPartnerListingCreatedDate,
                            ).format('MM/DD/YYYY')
                          : '--',
                      },
                      { content: partner.goingToMarketCount },
                      { content: partner.listingsActive },
                      { content: partner.listingsSubmitted },
                      { content: partner.listingsLeased },
                      { content: partner.activeWithdrawals },
                      { content: partner.goingToMarketWithdrawals },
                      { content: partner.gotchaGongCount },
                      { content: partner.gotchaGongPostLeaseCount },
                      {
                        content: partner.leaseFeePercentage
                          ? formatPercentage(partner.leaseFeePercentage)
                          : '--',
                      },
                      {
                        content:
                          partner.listingsSubmitted === 0
                            ? '--'
                            : formatPercentage(partner.listingsLeased / partner.listingsSubmitted),
                      },
                      {
                        content: expectedYearlyRevenue ? formatPrice(expectedYearlyRevenue) : '--',
                      },
                      {
                        content: partner.totalRevenue ? formatPrice(partner.totalRevenue) : '--',
                      },
                      {
                        content:
                          expectedYearlyRevenue && partner.totalRevenue
                            ? formatPercentage(partner.totalRevenue / expectedYearlyRevenue)
                            : '--',
                      },
                    ],
                  };
                })}
              />
              {dataTableApiState.inProgress && <div className="loading-ellipses" />}
              {editingPartner && (
                <SrPartnerEditModal
                  partner={editingPartner}
                  onSuccess={this._onEditPartnerModalSuccess}
                  onClose={this._onEditPartnerModalClose}
                  dispatch={dispatch}
                />
              )}
              {editingNotesPartner && (
                <SrPartnerNotesModal
                  partnerId={editingNotesPartner.id}
                  dispatch={dispatch}
                  currentUser={currentUser}
                  onClose={this._onEditPartnerNotesModalClose}
                />
              )}
            </div>
          )}
        </SrDataTableConnected>
      </div>
    );
  }
}

SrDataTableAdminPartners.displayName = 'SrDataTableAdminPartners';

export default SrDataTableAdminPartners;
